/**
 * Scale View Component
 * Displays the full M-Scale hardware with realistic 3D perspective
 * In dual-device mode, shows two separate 4x4 devices side-by-side
 */

import { useMScaleStore } from '../../store'
import { ScalePad } from './ScalePad'

interface ScaleViewProps {
  onPadClick?: (position: number) => void
  rotation?: number
}

export function ScaleView({ onPadClick, rotation = 0 }: ScaleViewProps = {}) {
  const { weights, groups, hardwareConfig, isDualDeviceMode, layoutConfig } = useMScaleStore()

  // Create group lookup map - maps each position to its group ID
  const positionToGroupId = new Map<number, number>()
  groups?.forEach((group) => {
    if (group.isValid) {
      // Map all positions in the group to the same group ID
      group.positions.forEach((position) => {
        positionToGroupId.set(position, group.groupId)
      })
    }
  })

  // Function to rotate a position based on rotation angle
  // position is local to the device (0-15), deviceOffset is added to get global position
  const rotatePosition = (position: number, angle: number, deviceOffset: number = 0): number => {
    const row = Math.floor(position / 4)
    const col = position % 4

    let newRow: number, newCol: number

    switch (angle) {
      case 90:
        newRow = 4 - 1 - col
        newCol = row
        break
      case 180:
        newRow = 4 - 1 - row
        newCol = 4 - 1 - col
        break
      case 270:
        newRow = col
        newCol = 4 - 1 - row
        break
      default:
        newRow = row
        newCol = col
    }

    return (newRow * 4 + newCol) + deviceOffset
  }

  // Render a single device (4x4 grid)
  const renderDevice = (deviceOffset: number, deviceRotation: number, label: string) => {
    const cells = []
    const gridRows = 4
    const gridCols = 4

    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const displayPosition = row * gridCols + col
        const actualPosition = rotatePosition(displayPosition, deviceRotation, deviceOffset)
        const weight = weights[actualPosition] ?? 0
        const isActive = actualPosition < (deviceOffset + 16)
        const pairId = positionToGroupId.get(actualPosition) ?? null

        cells.push(
          <ScalePad
            key={displayPosition}
            position={actualPosition}
            weight={weight}
            pairId={pairId}
            isActive={isActive}
            onClick={onPadClick ? () => onPadClick(actualPosition) : undefined}
          />
        )
      }
    }

    return (
      <div className="relative flex-1">
        {/* Base Shadow */}
        <div
          className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-[95%] h-4 sm:h-6 rounded-full opacity-30"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)',
            filter: 'blur(10px) sm:blur(15px)',
          }}
        />

        {/* Device Label */}
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
          {label}
        </div>

        {/* Main Scale Container */}
        <div
          className="relative"
          style={{
            transform: window.innerWidth < 640 ? 'rotateX(5deg) rotateZ(0deg)' : 'rotateX(6deg) rotateZ(0deg)',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Base Unit (dark gray/black foundation) */}
          <div
            className="absolute inset-0 rounded-lg"
            style={{
              backgroundColor: '#2d3748',
              transform: window.innerWidth < 640 ? 'translateZ(-6px)' : 'translateZ(-8px)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
            }}
          />

          {/* Frame (aluminum/gray border) */}
          <div
            className="relative p-1.5 sm:p-2 lg:p-3 rounded-lg"
            style={{
              backgroundColor: '#718096',
              transform: window.innerWidth < 640 ? 'translateZ(-3px)' : 'translateZ(-4px)',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
            }}
          >
            {/* Grid Container */}
            <div
              className="grid gap-1 sm:gap-1.5 lg:gap-2"
              style={{
                gridTemplateColumns: `repeat(4, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(4, minmax(0, 1fr))`,
              }}
            >
              {cells}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isDualDeviceMode) {
    // Dual-device mode: show two devices side-by-side
    const primaryRotation = layoutConfig.primaryRotation
    const secondaryRotation = layoutConfig.secondaryRotation
    const relativePosition = layoutConfig.relativePosition

    // Determine layout based on relative position
    const isHorizontalLayout = relativePosition === 'left' || relativePosition === 'right'
    const isPrimaryFirst = relativePosition === 'right' || relativePosition === 'below'

    return (
      <div className="relative w-full h-full flex items-center justify-center px-1 sm:px-2 py-4">
        {/* Shared perspective container for both devices */}
        <div
          className="w-full max-w-6xl mx-auto"
          style={{
            perspective: window.innerWidth < 640 ? '1600px' : '2400px',
            perspectiveOrigin: 'center center',
          }}
        >
          <div
            className={`flex ${isHorizontalLayout ? 'flex-row' : 'flex-col'} gap-3 sm:gap-4 lg:gap-6`}
          >
            {isPrimaryFirst ? (
              <>
                {renderDevice(0, primaryRotation, 'Primary')}
                {renderDevice(16, secondaryRotation, 'Secondary')}
              </>
            ) : (
              <>
                {renderDevice(16, secondaryRotation, 'Secondary')}
                {renderDevice(0, primaryRotation, 'Primary')}
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Single device mode
  const cells = []
  const gridRows = hardwareConfig?.gridRows ?? 4
  const gridCols = hardwareConfig?.gridCols ?? 4
  const activePositions = hardwareConfig?.activePositions ?? Array.from({ length: 16 }, (_, i) => i)

  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridCols; col++) {
      const displayPosition = row * gridCols + col
      const actualPosition = rotatePosition(displayPosition, rotation, 0)
      const weight = weights[actualPosition] ?? 0
      const isActive = activePositions.includes(actualPosition)
      const pairId = positionToGroupId.get(actualPosition) ?? null

      cells.push(
        <ScalePad
          key={displayPosition}
          position={actualPosition}
          weight={weight}
          pairId={pairId}
          isActive={isActive}
          onClick={onPadClick ? () => onPadClick(actualPosition) : undefined}
        />
      )
    }
  }

  return (
    <div className="relative w-full h-full flex items-center justify-center px-1 sm:px-2">
      {/* Scale Base Platform */}
      <div
        className="relative w-full mx-auto"
        style={{
          perspective: window.innerWidth < 640 ? '800px' : '1200px',
        }}
      >
        {/* Base Shadow */}
        <div
          className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-[95%] h-4 sm:h-6 rounded-full opacity-30"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)',
            filter: 'blur(10px) sm:blur(15px)',
          }}
        />

        {/* Main Scale Container */}
        <div
          className="relative"
          style={{
            transform: window.innerWidth < 640 ? 'rotateX(5deg) rotateZ(0deg)' : 'rotateX(6deg) rotateZ(0deg)',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Base Unit (dark gray/black foundation) */}
          <div
            className="absolute inset-0 rounded-lg"
            style={{
              backgroundColor: '#2d3748',
              transform: window.innerWidth < 640 ? 'translateZ(-6px)' : 'translateZ(-8px)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
            }}
          />

          {/* Frame (aluminum/gray border) */}
          <div
            className="relative p-1.5 sm:p-2 lg:p-3 rounded-lg"
            style={{
              backgroundColor: '#718096',
              transform: window.innerWidth < 640 ? 'translateZ(-3px)' : 'translateZ(-4px)',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
            }}
          >
            {/* Grid Container */}
            <div
              className="grid gap-1 sm:gap-1.5 lg:gap-2"
              style={{
                gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${gridRows}, minmax(0, 1fr))`,
              }}
            >
              {cells}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
